"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Trash2, Leaf, ArrowRight, CircleAlert as AlertCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const narrativeBlocks = [{
  icon: TrendingUp,
  title: "Solvent Procurement Volatility",
  subtitle: "Supply chain disruptions & surging unit costs",
  description: "High-volume analytical testing relies on continuous supplies of high-purity acetonitrile, methanol, and specialty buffers. Price spikes and unexpected delivery delays disrupt daily run schedules and increase operational expense across quality control laboratories.",
  metric: "+38%",
  metricLabel: "Average YoY solvent procurement price fluctuation"
}, {
  icon: Trash2,
  title: "Hazardous Waste Logistics",
  subtitle: "Stringent regulatory handling & rising disposal fees",
  description: "Managing thousands of liters of chemical effluent demands dedicated infrastructure, specialized handling labor, and compliant storage areas. Escalating transportation manifests and certified disposal surcharges create a compounding operational liability.",
  metric: "4.2x",
  metricLabel: "Disposal cost multiplier per liter versus initial purchase"
}, {
  icon: Leaf,
  title: "Scope 3 Environmental Pressures",
  subtitle: "Stricter ESG compliance and carbon audit targets",
  description: "Regulated life science and manufacturing organizations face intensifying investor and regulatory mandates to lower upstream Scope 3 emissions. Incinerating high-volume laboratory solvent waste directly impacts corporate sustainability compliance scores.",
  metric: "Rising",
  metricLabel: "Audit scrutiny on life science laboratory footprints"
}];
export default function HiddenCost() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="hiddencostchromatography" data-nav="light" className="bg-background text-foreground py-20 md:py-28 lg:py-32 border-b border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column (5 cols) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-80px"
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 lg:sticky lg:top-28 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20 bg-primary/10">
                <AlertCircle className="size-3.5 mr-1" />
                Operational Impact
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
              The Hidden Cost of High-Volume Chromatography
            </h2>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Standard analytical workflows generate compounding overhead beyond instrument depreciation. From solvent inventory fragility to regulated waste management, conventional chromatography exposes labs to severe economic and environmental liabilities.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="group">
                <Link to="/SolutionsPage">
                  <span>Explore Sustainable Alternatives</span>
                  <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/ContactPage">Talk to a Specialist</Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column (7 cols) - Narrative Blocks */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            {narrativeBlocks.map((block, i) => {
            const IconComponent = block.icon;
            return <motion.div key={block.title} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: "-60px"
            }} transition={{
              duration: 0.6,
              delay: shouldReduceMotion ? 0 : i * 0.15,
              ease: [0.16, 1, 0.3, 1]
            }} className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm transition-all hover:border-primary/40 hover:shadow-md">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <IconComponent className="size-6" />
                    </div>

                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 pb-1">
                        <h3 className="text-xl sm:text-2xl font-default font-bold text-primary">
                          {block.title}
                        </h3>
                      </div>

                      <p className="text-sm font-medium text-foreground mb-3">
                        {block.subtitle}
                      </p>

                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {block.description}
                      </p>

                      <Separator className="my-5" />

                      <div className="flex items-baseline gap-3">
                        <span className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
                          {block.metric}
                        </span>
                        <span className="text-xs sm:text-sm text-muted-foreground leading-snug">
                          {block.metricLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}